import { memo, useState } from "react";
import { Form, Input, Image } from "semantic-ui-react";
import Joi from "joi";
import "./_SignUpForm.scss";
import { Popup } from "controls";
import { useFieldset } from "controls/hooks";
import { SubscriptionLevelTable } from "../../common/SubscriptionLevelTable/SubscriptionLevelTable";
import { ToggleSwitch } from "components/atoms/ToggleSwitch/ToggleSwitch";
import { IncrementalButtonInput } from "components/atoms/IncrementalButtonInput/IncrementalButtonInput";
import { CardForm } from "components/User/Card/CardForm";
import { Button } from "components/atoms/Button/Button";
import { formatCurrency } from "controls/utils";
import { SUBSCRIPTION_OPTIONS, SUBSCRIPTION_VALUES, COUPON_TYPES, tableRows } from "./types";
import CouponForm from '../../CouponForm/CouponForm';

const signupForm = {
  email: "",
  name: "",
  password: "",
  subscriptionPlan: SUBSCRIPTION_OPTIONS.monthly,
  numberOfSubs: 1,
  coupon: "",
};

const signupSchema = {
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .max(50)
    .required()
    .label("Email"),
  name: Joi.string().max(30).required().label("Name"),
  password: Joi.string().label('Password').ruleset.pattern(/^(?=.*[a-z])(?=.*[0-9])(?=.{6,})/).max(30).message('Password needs to have a minimum length of 6, at least one letter and one number.'),
  subscriptionPlan: Joi.string().required().label("subscriptionPlan"),
  numberOfSubs: Joi.number().required().label("Number of Credits"),
  coupon: Joi.string().allow(""),
};

const mapSignUpData = ({ name, subscriptionPlan, ...loginData }) => ({
  ...loginData,
  subscriptionPlan: SUBSCRIPTION_VALUES[subscriptionPlan][process.env.REACT_APP_ENV],
  firstName: name.split(" ")[0],
  lastName: name.split(" ")[1] || "",
});

const SignUpForm = memo(() => {
  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState({});
  const [discount, setDiscount] = useState({
    type: COUPON_TYPES.percent,
    value: 0
  });

  const onSubmit = (data) => {
    setShowModal(true);
    setUserData(mapSignUpData(data));
  }

  const {
    fields: { name, email, password, subscriptionPlan, numberOfSubs, coupon },
    submit,
    loading,
  } = useFieldset({
    schema: signupSchema,
    source: signupForm,
    onSubmit: onSubmit,
  });

  return (
    <div className="flex flex-column mw9 center pt4 pb6">
        <div className='signUp__header'>
        <a className="signUp__header--image" href="https://goboxpdx.com/">
          <Image src={process.env.REACT_APP_LOGO} alt='logo' size='small' />
        </a>
        <span className="f4 mv3 b">Join our zero waste community!</span>
        </div>
      {showModal &&
        <CardForm 
          message={"This transaction is processed securely using Stripe Billing"}
          title={'Card Information'} 
          userInfo={userData} 
          onCancel={() => setShowModal(!showModal)}
        />  
      }
      <Form
        className="w-100 center mt3 signUp__form pa3"
        style={{ maxWidth: 500 }}
        onSubmit={submit}
      >
       <p className="f4 b center tc">Create your Bold Reuse account:</p>
        <Form.Field error={email.errored}>
          <label>What's your email?</label>
          <Popup
            message="Pleaser enter a valid email address"
            enabled={email.errored}
          >
            <Input
              fluid
              placeholder="Enter your email"
              value={email.value}
              onChange={email.onChange}
            />
          </Popup>
        </Form.Field>
        <Form.Field error={password.errored}>
          <label>What's your password?</label>
          <Popup message={password.message} enabled={password.errored}>
            <Input
              fluid
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password.value}
              onChange={password.onChange}
            />
          </Popup>
        </Form.Field>
        <Form.Field error={name.errored}>
          <label>What's your name?</label>
          <Popup
            message="Enter your first and last name"
            enabled={name.errored}
          >
            <Input
              fluid
              placeholder="First & Last Name"
              value={name.value}
              onChange={name.onChange}
            />
          </Popup>
        </Form.Field>
        <div className='signUp__section'>
        <p className="f4 b pb3 center tc">Select your Subscription type below:</p>
        <p className="f4 b center tc">the # of <span className='secondary-color'> credits</span> on your subscription is = to the total # of <span className='secondary-color'>reusables</span> you can have checked out at once</p>
        <SubscriptionLevelTable />
        </div>
        <Form.Field error={subscriptionPlan.errored}>
          <p className="f3 b center tc">Your Subscription Term:</p>
          <Popup
            message="Select a subscription type"
            enabled={subscriptionPlan.errored}
          >
            <ToggleSwitch
              onChange={subscriptionPlan.onChange}
              value={subscriptionPlan.value}
              optionA={SUBSCRIPTION_OPTIONS.monthly}
              optionB={SUBSCRIPTION_OPTIONS.annual}
            />
          </Popup>
        </Form.Field>
        <Form.Field error={numberOfSubs.errored}>
          <p className="center tc f4 b">Number of credits</p>
          <IncrementalButtonInput
            value={numberOfSubs.value}
            onChange={numberOfSubs.onChange}
            min={1}
            max={subscriptionPlan.value === SUBSCRIPTION_OPTIONS.monthly ? 2 : 4}
          />
        </Form.Field>
        <p className='f4 b tc signUp__total'>
        Total cost: 
          <span className='secondary-color f3 fw5'> 
            {
              formatCurrency(discount.type !== COUPON_TYPES.price ?

              tableRows[numberOfSubs.value || 1][subscriptionPlan.value === SUBSCRIPTION_OPTIONS.monthly ? 1 : 2 ]?.value * (subscriptionPlan.value === SUBSCRIPTION_OPTIONS.annual ? 1 - discount.value : 1) 

              : tableRows[numberOfSubs.value || 1][subscriptionPlan.value === SUBSCRIPTION_OPTIONS.monthly ? 1 : 2 ]?.value - (subscriptionPlan.value === SUBSCRIPTION_OPTIONS.annual ? discount.value : 0 )
              )
              }
              /{subscriptionPlan.value === SUBSCRIPTION_OPTIONS.monthly ? 'month' : 'year'}
          </span>
         </p>
          <CouponForm setDiscount={setDiscount} value={coupon.value} onChange={coupon.onChange} discount={discount} subscriptionPlan={subscriptionPlan.value} />

        <Button text="Next" disabled={loading} onClick={submit} extraStyle='flex center ph5' />
      </Form>
    </div>
  );
});

export default SignUpForm;
